import { IdentityTypes, MainlandSupplementaryInfoAuditStatus } from "@/interfaces/enums";
import { Answer } from "@/interfaces/surveyTypes";
import rfStore from "@/utils/rfStore";

export const CN_ACCOUNT_OPENING_WHITELIST_USER_IDS = [
	"7465275250038147271",
	"7465268357710350532",
	"7465964654880883936",
	"7419260783852392004",
	"7468511171877071237",
	"7468154615482346051",
] as const;

export const MAINLAND_NATIONALITY_BLOCK_ENABLED = false;

export const MAINLAND_IP_BLOCK_SETTING_KEY = "compliance.cn.accountopen.block.open";

export const CN_RESTRICTION_COUNTRY = "CN";

export type AppSettingItem = { key: string; value: string };

export const isCnAccountOpeningWhitelisted = (userId?: string) =>
	!!userId &&
	CN_ACCOUNT_OPENING_WHITELIST_USER_IDS.includes(userId as (typeof CN_ACCOUNT_OPENING_WHITELIST_USER_IDS)[number]);

export const isMainlandIpBlockEnabled = (settings?: AppSettingItem[]) =>
	settings?.find((item) => item.key === MAINLAND_IP_BLOCK_SETTING_KEY)?.value === "true";

export const DEBUG_SIMULATE_NON_CN_STORAGE_KEY = "rf_debug_mainland_simulate_non_cn";

/** App 调试开关「模拟非大陆用户」，仅测试环境生效 */
export const isDebugSimulateNonCn = () => {
	if (process.env.NEXT_PUBLIC_APP_ENV === "production" || typeof window === "undefined") return false;
	try {
		return window.localStorage.getItem(DEBUG_SIMULATE_NON_CN_STORAGE_KEY) === "1";
	} catch (error) {
		return false;
	}
};

export const getIpRegionFromSettings = (settings?: AppSettingItem[]) => {
	if (isDebugSimulateNonCn()) return undefined;
	return settings?.find((item) => item.key === "country")?.value;
};

export const shouldBlockMainlandAccountOpening = (nationality: string) =>
	MAINLAND_NATIONALITY_BLOCK_ENABLED &&
	nationality === "CN" &&
	!isCnAccountOpeningWhitelisted(rfStore.getRFState()?.userId);

export const shouldBlockMainlandAccountOpeningByIp = (ipRegion?: string, settings?: AppSettingItem[]) =>
	isMainlandIpBlockEnabled(settings) &&
	ipRegion === CN_RESTRICTION_COUNTRY &&
	!isCnAccountOpeningWhitelisted(rfStore.getRFState()?.userId);

export function mightNeedMainlandSupplementByProfile(params: {
	nationality?: string;
	documentType?: string | number;
}): boolean {
	const docType = Number(params.documentType);
	return (
		params.nationality === "CN" &&
		(docType === IdentityTypes.ID_CARD || docType === IdentityTypes.PASSPORT)
	);
}

export function shouldShowMainlandSupplementInFlow(params: {
	nationality?: string;
	documentType?: string | number;
	auditStatus?: MainlandSupplementaryInfoAuditStatus;
}): boolean {
	const docType = Number(params.documentType);
	const matchesProfile =
		params.nationality === "CN" &&
		(docType === IdentityTypes.ID_CARD || docType === IdentityTypes.PASSPORT);

	if (!matchesProfile) return false;

	// 已提交待审 / 已通过 → 跳过补充资料页
	if (
		params.auditStatus === MainlandSupplementaryInfoAuditStatus.AUDIT_PASS ||
		params.auditStatus === MainlandSupplementaryInfoAuditStatus.AUDIT_PENDING
	) {
		return false;
	}

	// UN_COMMIT / AUDIT_REJECT / NEED_NOT_SUBMIT(9) / undefined 均需走补充资料
	return true;
}

export function shouldResumeMainlandSupplementPage(params: {
	nationality?: string;
	documentType?: string | number;
	auditStatus?: MainlandSupplementaryInfoAuditStatus;
	mainlandRejectReasons?: string[];
	supplementIssue?: string;
}): boolean {
	if (!mightNeedMainlandSupplementByProfile(params)) return false;

	if (params.auditStatus === MainlandSupplementaryInfoAuditStatus.AUDIT_PASS) return false;

	if (params.supplementIssue === "supp_info_china") return true;

	if (params.mainlandRejectReasons?.length) return true;

	return shouldShowMainlandSupplementInFlow(params);
}

const PERSONAL_INFO_FIELDS = ["documentNumber", "firstName", "lastName", "gender", "birthday", "countryOfResidence", "address"];

export function hasCompletedPersonalInfo(answers: Answer[]): boolean {
	return PERSONAL_INFO_FIELDS.every((field) => answers.some((el) => el.field === field));
}

export function getPageAfterSupplementSubmit(answers: Answer[], supplementIssue?: string): number | null {
	if (!hasCompletedPersonalInfo(answers)) {
		return 7;
	}
	if (supplementIssue === "supp_info_china") {
		return null;
	}
	return 13;
}
